import React, { useMemo } from 'react';
import { ReactFlow, Controls, Background, useNodesState, useEdgesState, BackgroundVariant, Node, Edge, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeftIcon, MessageSquareIcon, SmartphoneIcon, GlobeIcon, MailIcon, PhoneIcon, BrainIcon, DatabaseIcon, ActivityIcon, WalletIcon, ShieldIcon, CoinsIcon, SettingsIcon } from 'lucide-react';
import AnimatedEdge from './AnimatedEdge';
// ============================================================================
// MOCK DATA PER SERVICE
// ============================================================================
interface ChannelData {
  id: string;
  name: string;
  icon: 'chat' | 'mobile' | 'api' | 'email' | 'whatsapp';
  color: string;
  requestsPerDay: number;
}
interface KnowledgeData {
  id: string;
  name: string;
  documents: number;
  status: 'Attivo' | 'In aggiornamento';
}
interface ServiceDetailData {
  channels: ChannelData[];
  knowledge: KnowledgeData[];
}
const SERVICE_DETAIL_DATA: Record<string, ServiceDetailData> = {
  s1: {
    channels: [{
      id: 'ch1',
      name: 'Web Chat',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 680
    }, {
      id: 'ch2',
      name: 'WhatsApp',
      icon: 'whatsapp',
      color: '#25d366',
      requestsPerDay: 320
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 150
    }, {
      id: 'ch4',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'FAQ Database',
      documents: 142,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Catalog',
      documents: 89,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Return Policy',
      documents: 34,
      status: 'Attivo'
    }]
  },
  s2: {
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 4200
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 1100
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 200
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Knowledge Base',
      documents: 1250,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Documentation',
      documents: 340,
      status: 'Attivo'
    }]
  },
  s3: {
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 500
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 200
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Content Guidelines',
      documents: 56,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Blacklist Terms',
      documents: 1200,
      status: 'In aggiornamento'
    }]
  },
  s4: {
    channels: [{
      id: 'ch1',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 380
    }, {
      id: 'ch2',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 150
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 30
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Training Materials',
      documents: 245,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Company Policies',
      documents: 67,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Onboarding Guide',
      documents: 23,
      status: 'Attivo'
    }]
  }
};
const getDefaultServiceData = (serviceType: string): ServiceDetailData => ({
  channels: [{
    id: 'ch1',
    name: 'API',
    icon: 'api',
    color: '#8b5cf6',
    requestsPerDay: 300
  }, {
    id: 'ch2',
    name: 'Web Platform',
    icon: 'chat',
    color: '#3b82f6',
    requestsPerDay: 150
  }],
  knowledge: [{
    id: 'kb1',
    name: 'General Knowledge',
    documents: 100,
    status: 'Attivo'
  }, {
    id: 'kb2',
    name: 'Domain Data',
    documents: 50,
    status: 'Attivo'
  }]
});
// ============================================================================
// NODE COMPONENTS
// ============================================================================
const ChannelIcon = ({
  icon,
  className,
  style
}: {
  icon: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  switch (icon) {
    case 'chat':
      return <MessageSquareIcon className={className} style={style} data-id="element-148" />;
    case 'mobile':
      return <SmartphoneIcon className={className} style={style} data-id="element-149" />;
    case 'api':
      return <GlobeIcon className={className} style={style} data-id="element-150" />;
    case 'email':
      return <MailIcon className={className} style={style} data-id="element-151" />;
    case 'whatsapp':
      return <PhoneIcon className={className} style={style} data-id="element-152" />;
    default:
      return <GlobeIcon className={className} style={style} data-id="element-153" />;
  }
};
const ChannelNode = ({
  data
}: {
  data: any;
}) => {
  return <div className="relative flex items-center w-[200px] bg-[#1f1f1f] border border-white/[0.12] rounded-xl overflow-hidden shadow-lg hover:border-white/[0.20] transition-all" data-id="element-154">
      <div className="w-1.5 h-full absolute left-0 top-0 bottom-0" style={{
      backgroundColor: data.color
    }} data-id="element-155" />
      <div className="flex items-center gap-3 pl-5 pr-4 py-4" data-id="element-156">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
        backgroundColor: `${data.color}15`
      }} data-id="element-157">
          <ChannelIcon icon={data.icon} className="w-4 h-4" style={{
          color: data.color
        }} data-id="element-158" />
        </div>
        <div className="flex flex-col min-w-0" data-id="element-159">
          <span className="text-white/90 text-[13px] font-medium truncate" data-id="element-160">
            {data.label}
          </span>
          <span className="text-white/40 text-[11px]" data-id="element-161">
            {data.requestsPerDay.toLocaleString()}/day
          </span>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-white/20 !w-2.5 !h-2.5 !border-0 !right-[-5px]" data-id="element-162" />
    </div>;
};
const ServiceMainNode = ({
  data
}: {
  data: any;
}) => {
  const healthColor = data.health === 'healthy' ? 'bg-[#8eff01]' : data.health === 'warning' ? 'bg-amber-500' : 'bg-red-500';
  return <div className="relative w-[420px] bg-[#1f1f1f] border border-white/[0.14] rounded-2xl shadow-2xl" data-id="element-163">
      <Handle type="target" position={Position.Left} className="!bg-white/20 !w-3 !h-3 !border-0 !left-[-6px]" data-id="element-164" />
      <Handle type="source" position={Position.Right} id="knowledge" className="!bg-purple-500/50 !w-3 !h-3 !border-0 !right-[-6px] !top-[40%]" data-id="element-165" />

      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-white/[0.10]" data-id="element-166">
        <div className="flex items-center gap-3 mb-2" data-id="element-167">
          <div className={`w-3.5 h-3.5 rounded-full ${healthColor} shadow-[0_0_12px_currentColor]`} data-id="element-168" />
          <span className="text-white text-[20px] font-semibold" data-id="element-169">
            {data.label}
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-170">
          <span className="px-2.5 py-1 bg-white/[0.06] rounded-md text-[11px] text-white/50 font-medium uppercase tracking-wider" data-id="element-171">
            {data.type}
          </span>
          <span className="text-white/30" data-id="element-172">•</span>
          <span className="text-white/40 text-[12px]" data-id="element-173">{data.model}</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 p-5" data-id="element-174">
        <div className="p-3 bg-white/[0.05] rounded-xl border border-white/[0.08]" data-id="element-175">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-176">
            <WalletIcon className="w-3.5 h-3.5 text-white/25" data-id="element-177" />
            <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-178">
              Costo
            </span>
          </div>
          <span className="text-[18px] font-semibold text-white" data-id="element-179">
            €{data.cost}/mo
          </span>
        </div>
        <div className="p-3 bg-white/[0.05] rounded-xl border border-white/[0.08]" data-id="element-180">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-181">
            <ActivityIcon className="w-3.5 h-3.5 text-white/25" data-id="element-182" />
            <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-183">
              Richieste
            </span>
          </div>
          <span className="text-[18px] font-semibold text-white" data-id="element-184">
            {data.requestsPerDay.toLocaleString()}/d
          </span>
        </div>
        <div className="p-3 bg-white/[0.05] rounded-xl border border-white/[0.08]" data-id="element-185">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-186">
            <ShieldIcon className="w-3.5 h-3.5 text-white/25" data-id="element-187" />
            <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-188">
              Compliance
            </span>
          </div>
          <span className={`text-[18px] font-semibold ${data.compliance >= 80 ? 'text-[#8eff01]' : 'text-amber-400'}`} data-id="element-189">
            {data.compliance}%
          </span>
        </div>
        <div className="p-3 bg-white/[0.05] rounded-xl border border-white/[0.08]" data-id="element-190">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-191">
            <CoinsIcon className="w-3.5 h-3.5 text-white/25" data-id="element-192" />
            <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-193">
              Tokens
            </span>
          </div>
          <span className="text-[18px] font-semibold text-white" data-id="element-194">
            {data.tokens}
          </span>
        </div>
      </div>

      {/* Gestisci Button */}
      <div className="px-5 pb-5" data-id="element-195">
        <button onClick={e => {
        e.stopPropagation();
        if (data.onNavigateToDetail) data.onNavigateToDetail();
      }} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/[0.07] hover:bg-white/[0.12] border border-white/[0.12] hover:border-white/[0.20] rounded-xl transition-all duration-200 group" data-id="element-196">
          <SettingsIcon className="w-4 h-4 text-white/40 group-hover:text-[#8eff01] transition-colors" data-id="element-197" />
          <span className="text-[13px] font-medium text-white/60 group-hover:text-white transition-colors" data-id="element-198">
            Gestisci Servizio
          </span>
        </button>
      </div>
    </div>;
};
const KnowledgeNode = ({
  data
}: {
  data: any;
}) => {
  const isActive = data.status === 'Attivo';
  return <div className="relative w-[240px] bg-[#1f1f1f] border border-white/[0.12] rounded-xl shadow-lg hover:border-white/[0.20] transition-all" data-id="element-199">
      <Handle type="target" position={Position.Left} className="!bg-purple-500/50 !w-2.5 !h-2.5 !border-0 !left-[-5px]" data-id="element-200" />
      <div className="p-4" data-id="element-201">
        <div className="flex items-center gap-3 mb-3" data-id="element-202">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center" data-id="element-203">
            <BrainIcon className="w-5 h-5 text-purple-400" data-id="element-204" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-205">
            <span className="text-white/90 text-[14px] font-medium block truncate" data-id="element-206">
              {data.label}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-207">
              <DatabaseIcon className="w-3 h-3 text-white/30" data-id="element-208" />
              <span className="text-white/40 text-[11px]" data-id="element-209">
                {data.documents} documenti
              </span>
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium ${isActive ? 'bg-[#8eff01]/10 text-[#8eff01]' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-210">
          <div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#8eff01]' : 'bg-amber-400'}`} data-id="element-211" />
          {data.status}
        </div>
      </div>
    </div>;
};
// ============================================================================
// MAIN COMPONENT
// ============================================================================
interface ServiceDetailCanvasProps {
  service: {
    id: string;
    name: string;
    type: string;
    model: string;
    health: string;
    cost: number;
    compliance: number;
    description: string;
    requestsPerDay: number;
    tokens?: string;
  };
  onBack: () => void;
  onNavigateToDetail?: (serviceId: string) => void;
}
export function ServiceDetailCanvas({
  service,
  onBack,
  onNavigateToDetail
}: ServiceDetailCanvasProps) {
  const serviceData = SERVICE_DETAIL_DATA[service.id] || getDefaultServiceData(service.type);
  const detailNodes: Node[] = useMemo(() => {
    const nodes: Node[] = [];
    serviceData.channels.forEach((channel, i) => {
      nodes.push({
        id: `channel-${channel.id}`,
        type: 'channelNode',
        position: {
          x: 50,
          y: 120 + i * 100
        },
        data: {
          label: channel.name,
          icon: channel.icon,
          color: channel.color,
          requestsPerDay: channel.requestsPerDay
        }
      });
    });
    const centerY = 120 + (serviceData.channels.length - 1) * 100 / 2 - 100;
    nodes.push({
      id: 'service-main',
      type: 'serviceMainNode',
      position: {
        x: 380,
        y: centerY
      },
      data: {
        label: service.name,
        type: service.type,
        model: service.model,
        health: service.health,
        cost: service.cost,
        compliance: service.compliance,
        requestsPerDay: service.requestsPerDay,
        tokens: service.tokens || '450K',
        onNavigateToDetail: () => onNavigateToDetail?.(service.id)
      }
    });
    serviceData.knowledge.forEach((kb, i) => {
      nodes.push({
        id: `knowledge-${kb.id}`,
        type: 'knowledgeNode',
        position: {
          x: 920,
          y: 100 + i * 110
        },
        data: {
          label: kb.name,
          documents: kb.documents,
          status: kb.status
        }
      });
    });
    return nodes;
  }, [service, serviceData, onNavigateToDetail]);
  const detailEdges: Edge[] = useMemo(() => {
    const edges: Edge[] = [];
    serviceData.channels.forEach(channel => {
      edges.push({
        id: `e-channel-${channel.id}-service`,
        source: `channel-${channel.id}`,
        target: 'service-main',
        type: 'animatedEdge',
        data: {
          intensity: Math.max(1, Math.min(5, Math.round(channel.requestsPerDay / 200))),
          dotColor: '#ffffff'
        }
      });
    });
    serviceData.knowledge.forEach(kb => {
      edges.push({
        id: `e-service-knowledge-${kb.id}`,
        source: 'service-main',
        sourceHandle: 'knowledge',
        target: `knowledge-${kb.id}`,
        type: 'animatedEdge',
        data: {
          intensity: 2,
          dotColor: '#a855f7'
        }
      });
    });
    return edges;
  }, [serviceData]);
  const [nodes,, onNodesChange] = useNodesState(detailNodes);
  const [edges,, onEdgesChange] = useEdgesState(detailEdges);
  const nodeTypes = useMemo(() => ({
    channelNode: ChannelNode,
    serviceMainNode: ServiceMainNode,
    knowledgeNode: KnowledgeNode
  }), []);
  const edgeTypes = useMemo(() => ({
    animatedEdge: AnimatedEdge
  }), []);
  return <div className="w-full h-full relative" data-id="element-212">
      {/* Breadcrumb Navigation */}
      <div className="absolute top-4 left-4 z-20" data-id="element-213">
        <button onClick={onBack} className="flex items-center gap-2 px-3 py-2 bg-[#272727]/95 backdrop-blur-xl border border-white/[0.14] rounded-xl hover:border-white/[0.22] hover:bg-[#272727] transition-all duration-200 shadow-lg group" data-id="element-214">
          <ArrowLeftIcon className="w-3.5 h-3.5 text-white/50 group-hover:text-[#8eff01] transition-colors" data-id="element-215" />
          <div className="flex items-center gap-2 text-[12px]" data-id="element-216">
            <span className="text-white/40" data-id="element-217">AI Map</span>
            <span className="text-white/20" data-id="element-218">/</span>
            <span className="text-white font-medium" data-id="element-219">{service.name}</span>
          </div>
        </button>
      </div>

      {/* Service Info Badge */}
      <div className="absolute top-4 right-4 z-20" data-id="element-220">
        <div className="flex items-center gap-3 px-3 py-2 bg-[#272727]/95 backdrop-blur-xl border border-white/[0.14] rounded-xl shadow-lg" data-id="element-221">
          <div className={`w-2.5 h-2.5 rounded-full ${service.health === 'healthy' ? 'bg-[#8eff01]' : service.health === 'warning' ? 'bg-amber-500' : 'bg-red-500'} shadow-[0_0_8px_currentColor]`} data-id="element-222" />
          <span className="text-white/70 text-[12px] font-medium" data-id="element-223">
            {service.type}
          </span>
          <span className="text-white/30" data-id="element-224">•</span>
          <span className="text-white/50 text-[12px]" data-id="element-225">{service.model}</span>
          <span className="text-white/30" data-id="element-226">•</span>
          <span className="text-white/50 text-[12px]" data-id="element-227">€{service.cost}/mo</span>
        </div>
      </div>

      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView className="bg-[#0a0a0a]" minZoom={0.4} maxZoom={1.5} fitViewOptions={{
      padding: 0.2
    }} data-id="element-228">
        <Background color="rgba(255, 255, 255, 0.25)" gap={20} variant={BackgroundVariant.Dots} size={1.5} data-id="element-229" />
        <Controls className="bg-[#1a1a1a] border border-white/[0.1] text-white fill-white" data-id="element-230" />
      </ReactFlow>
    </div>;
}